import ExcelJS from "exceljs";
import { compareTemplateItems } from "./compareTemplateItems";

const HEADER_FILL = "FF82CAEC";
const SUBCATEGORY_FILL = "FFEAF5FB";
const ASSET_FILL = "FFE6F4FA";
const SUBKPI_FILL = "FFF5EFE8";
const ASSET_SYMBOL = "↳";
const SUBKPI_SYMBOL = "•";
const BORDER_COLOR = "FF000000";
const TEXT_RED = "FFCC0000";
const FONT_NAME = "Arial";
const FONT_SIZE = 11;

/**
 * Browser port of the backend `buildTemplateWorkbook`.
 * KPIs are ordered by module, then category, then sub category, but only the
 * Sub Category grouping column is rendered — module and category drive the row
 * order without being shown. The Sub Category cells merge per contiguous run,
 * bounded by category changes so a run never bleeds across two categories.
 * Each module's rows are re-sorted with `compareTemplateItems` before
 * rendering, so those merged cells stay contiguous even if a caller passes
 * items in some other order. Returns a Blob ready to be downloaded via an
 * <a download>.
 */
export const buildTemplateWorkbook = async (sortedModules, periods) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Data Entry");
  sheet.views = [{ showGridLines: false }];

  const periodHeaders = periods.map(
    (p, i) => (p.label && p.label.trim()) || `Period ${i + 1}`,
  );

  const MARGIN_COL = 1;
  const SUBCATEGORY_COL = 2;
  const KPI_COL = 3;
  const UNIT_COL = 4;
  const EVIDENCE_COL = 5;
  const periodColStart = 6;
  const totalColIdx = periodColStart + periodHeaders.length;
  const lastDataCol = totalColIdx;

  const KPI_WIDTH = 50;
  const GROUP_WIDTH = Math.round((KPI_WIDTH * 2) / 3);
  sheet.getColumn(MARGIN_COL).width = 4;
  sheet.getColumn(SUBCATEGORY_COL).width = GROUP_WIDTH;
  sheet.getColumn(KPI_COL).width = KPI_WIDTH;
  sheet.getColumn(UNIT_COL).width = 13;
  sheet.getColumn(EVIDENCE_COL).width = GROUP_WIDTH;
  for (let i = 0; i < periodHeaders.length; i++) {
    sheet.getColumn(periodColStart + i).width = periodHeaders[i].length + 2;
  }
  sheet.getColumn(totalColIdx).width = 14;

  const blackBorder = {
    top: { style: "thin", color: { argb: BORDER_COLOR } },
    bottom: { style: "thin", color: { argb: BORDER_COLOR } },
    left: { style: "thin", color: { argb: BORDER_COLOR } },
    right: { style: "thin", color: { argb: BORDER_COLOR } },
  };
  const baseFont = { name: FONT_NAME, size: FONT_SIZE, color: { argb: "FF000000" } };
  const boldFont = { ...baseFont, bold: true };

  sheet.getRow(1).height = 8;
  sheet.getRow(2).height = 8;

  const noticeRowRef = sheet.getRow(3);
  noticeRowRef.height = 20;
  const noticeCell = noticeRowRef.getCell(SUBCATEGORY_COL);
  noticeCell.value = "Fill only the Empty Cells (If not available, mark as 0)";
  noticeCell.font = { name: FONT_NAME, size: FONT_SIZE, bold: true, color: { argb: TEXT_RED } };
  noticeCell.alignment = { vertical: "middle", horizontal: "left" };

  const HEADER_ROW = 4;
  const headerLabels = ["Sub Category", "KPI", "Unit", "Required Evidences", ...periodHeaders, "Total"];
  const headerRowRef = sheet.getRow(HEADER_ROW);
  headerRowRef.height = 28;
  for (let i = 0; i < headerLabels.length; i++) {
    const cell = headerRowRef.getCell(SUBCATEGORY_COL + i);
    cell.value = headerLabels[i];
    cell.font = boldFont;
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
    cell.border = blackBorder;
  }

  const writeDataRow = (excelRow, opts) => {
    excelRow.getCell(KPI_COL).value = opts.title;
    excelRow.getCell(UNIT_COL).value = opts.unitValue;
    if (opts.evidences !== undefined) excelRow.getCell(EVIDENCE_COL).value = opts.evidences;

    if (opts.unitOptions.length > 0) {
      excelRow.getCell(UNIT_COL).dataValidation = {
        type: "list",
        allowBlank: false,
        formulae: [`"${opts.unitOptions.join(",")}"`],
        showErrorMessage: true,
        errorTitle: "Invalid unit",
        error: `Allowed: ${opts.unitOptions.join(", ")}`,
      };
    }

    for (let c = SUBCATEGORY_COL; c <= lastDataCol; c++) {
      const cell = excelRow.getCell(c);
      cell.font = baseFont;
      cell.border = blackBorder;
      if (opts.rowFill) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: opts.rowFill } };
      }
    }

    excelRow.getCell(KPI_COL).alignment = {
      wrapText: true,
      vertical: "middle",
      horizontal: "left",
      indent: opts.titleIndent ?? 0,
    };
    excelRow.getCell(UNIT_COL).alignment = { wrapText: true, vertical: "middle", horizontal: "center" };
    excelRow.getCell(EVIDENCE_COL).alignment = { wrapText: true, vertical: "middle", horizontal: "left" };

    for (let p = 0; p < periodHeaders.length; p++) {
      const cell = excelRow.getCell(periodColStart + p);
      cell.numFmt = "#,##0.##########";
      cell.alignment = { horizontal: "right", vertical: "middle" };
    }

    if (periodHeaders.length > 0) {
      const firstAddr = excelRow.getCell(periodColStart).address;
      const lastAddr = excelRow.getCell(periodColStart + periodHeaders.length - 1).address;
      const totalCell = excelRow.getCell(totalColIdx);
      totalCell.value = { formula: `SUM(${firstAddr}:${lastAddr})`, result: 0 };
      totalCell.numFmt = "#,##0.##########";
      totalCell.font = boldFont;
      totalCell.alignment = { horizontal: "center", vertical: "middle" };
    }
  };

  const writeGroupCell = (col, startRow, endRow, label, fill) => {
    if (endRow < startRow) return;
    if (endRow > startRow) sheet.mergeCells(startRow, col, endRow, col);
    const cell = sheet.getCell(startRow, col);
    cell.value = label;
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
    cell.font = boldFont;
    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true, indent: 1 };
    cell.border = blackBorder;
  };

  let rowIdx = HEADER_ROW + 1;
  let firstModule = true;

  const orderedModules = sortedModules.map(([moduleName, items]) => [
    moduleName,
    [...items].sort(compareTemplateItems),
  ]);

  for (const [, items] of orderedModules) {
    if (!firstModule) {
      sheet.getRow(rowIdx).height = 11;
      rowIdx++;
    }
    firstModule = false;

    let currentCategory = null;
    let subCategoryStartRow = rowIdx;
    let currentSubCategory = null;

    for (const item of items) {
      const category = item.category || "Other";
      const subCategory = item.subCategory || "Other";
      if (currentCategory === null) {
        currentCategory = category;
      } else if (category !== currentCategory) {
        writeGroupCell(SUBCATEGORY_COL, subCategoryStartRow, rowIdx - 1, currentSubCategory, SUBCATEGORY_FILL);
        currentCategory = category;
        currentSubCategory = null;
      }

      if (currentSubCategory === null) {
        currentSubCategory = subCategory;
        subCategoryStartRow = rowIdx;
      } else if (subCategory !== currentSubCategory) {
        writeGroupCell(SUBCATEGORY_COL, subCategoryStartRow, rowIdx - 1, currentSubCategory, SUBCATEGORY_FILL);
        currentSubCategory = subCategory;
        subCategoryStartRow = rowIdx;
      }

      const kpiRowStart = rowIdx;
      const evidencesText = item.evidences.join(", ");

      const kpiRow = sheet.getRow(rowIdx);
      kpiRow.height = Math.max(22, Math.min(60, 16 + Math.floor(item.title.length / 50) * 14));
      writeDataRow(kpiRow, {
        title: item.title,
        unitValue: item.defaultUnit,
        unitOptions: item.supportedUnits.map((u) => u.unit).filter(Boolean),
        evidences: evidencesText,
      });
      rowIdx++;

      for (const asset of item.assets) {
        const assetRow = sheet.getRow(rowIdx);
        assetRow.height = Math.max(22, Math.min(60, 16 + Math.floor(asset.title.length / 50) * 14));
        const symbol = asset.kind === "asset" ? ASSET_SYMBOL : SUBKPI_SYMBOL;
        writeDataRow(assetRow, {
          title: `${symbol} ${asset.title}`,
          titleIndent: 2,
          unitValue: asset.defaultUnit,
          unitOptions: asset.supportedUnits.map((u) => u.unit).filter(Boolean),
          rowFill: asset.kind === "asset" ? ASSET_FILL : SUBKPI_FILL,
        });
        rowIdx++;
      }

      const kpiRowEnd = rowIdx - 1;
      if (kpiRowEnd > kpiRowStart) {
        sheet.mergeCells(kpiRowStart, EVIDENCE_COL, kpiRowEnd, EVIDENCE_COL);
        const merged = sheet.getCell(kpiRowStart, EVIDENCE_COL);
        merged.alignment = { wrapText: true, vertical: "middle", horizontal: "left" };
        merged.border = blackBorder;
      }
    }

    if (currentSubCategory !== null) {
      writeGroupCell(SUBCATEGORY_COL, subCategoryStartRow, rowIdx - 1, currentSubCategory, SUBCATEGORY_FILL);
    }
  }

  const buf = await workbook.xlsx.writeBuffer();
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};
